import React from "react";
import { Button } from "@/components/ui/button";
import UserProfile from "@/pages/profile/User";
import { Card } from "@/components/ui/card";
import CtaCard from "@/components/banners/CtaCard";
import researchImage from "/images/research.png";
import usePlanStore from "@/stores/planStore";
import PerformanceSummaryCard from "@/components/ui/performance-summary-card";
import PropertyStatusCard from "@/components/ui/property-status-card";
import {
  contactsParamsAtom,
  getPipelineSummaryRange,
  usePipelineSummary,
} from "@/_actions/contactActions";
import { useSetAtom } from "jotai";
import { useNavigate } from "react-router";

import { useFeatureFlagPayload, useFeatureFlagEnabled } from "posthog-js/react";

export default function Actions() {
  const isTestEnabled = useFeatureFlagEnabled("launch-research-test");
  const test = useFeatureFlagPayload("launch-research-test");
  const { canPublish } = usePlanStore((state) => state);
  const [pipelinePeriod, setPipelinePeriod] = React.useState(7);
  const createdRange = React.useMemo(
    () => getPipelineSummaryRange(pipelinePeriod),
    [pipelinePeriod],
  );
  const { data: pipelineSummary, isLoading, error } = usePipelineSummary(createdRange);
  const setContactParams = useSetAtom(contactsParamsAtom);
  const navigate = useNavigate();

  const openPipelineStage = (stageId) => {
    const pipelineId = pipelineSummary?.pipeline?.id;
    if (pipelineSummary?.state !== "ok" || !pipelineId) return;

    const resolvedRange = pipelineSummary.created_range;
    setContactParams({
      page: 1,
      status: "1,2",
      pipeline_id: String(pipelineId),
      pipeline_stage: [String(stageId)],
      created_range:
        resolvedRange?.start && resolvedRange?.end
          ? `${resolvedRange.start},${resolvedRange.end}`
          : createdRange,
    });
    navigate("/contacts");
  };

  return (
    <div className="bg-gray-50 py-16 sm:py-20 min-h-screen">
      <div className="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
        <div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl text-pretty">
            Empieza a conectar con tus clientes
          </h1>
          <p className="text-xl text-gray-500 text-pretty font-regular">
            Te damos la bienvenida a Casas y Terrenos
          </p>
        </div>
        <div className="mt-10 space-y-6 sm:mt-16">
          {/*Performance Summary Card*/}
          <div>
            <PerformanceSummaryCard />
          </div>

          <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
            <Card className="min-w-0 min-h-80 rounded-3xl border border-gray-200 p-8 shadow-none">
              <UserProfile dashboard />
            </Card>
            <PropertyStatusCard
              summary={pipelineSummary}
              isLoading={isLoading}
              hasError={Boolean(error)}
              periodDays={pipelinePeriod}
              onPeriodChange={setPipelinePeriod}
              onStageSelect={openPipelineStage}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Create Property Card */}
            <div className="relative min-w-0">
              <div className="absolute inset-px rounded-lg bg-white"></div>
              <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)]">
                <div className="px-8 pt-8 sm:px-10 sm:pt-10">
                  <p className="mt-2 text-lg font-medium tracking-tight text-gray-950 max-lg:text-center">
                    Crea una propiedad
                  </p>
                  <p className="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                    ¡Llegó el momento de vender! Publica tus propiedades en minutos y conéctalas con
                    miles de compradores interesados. Elige el plan que mejor se adapte a tu
                    inventario y empieza a generar prospectos hoy mismo.
                  </p>
                </div>
                <div className="flex flex-1 items-center justify-center px-8 max-lg:pt-10 max-lg:pb-12 sm:px-10 lg:pb-2">
                  <a
                    href={canPublish ? "/properties/create" : undefined}
                    className={`w-full ${!canPublish ? "pointer-events-none opacity-60" : ""}`}
                    onClick={(e) => {
                      if (!canPublish) e.preventDefault();
                    }}
                    aria-disabled={!canPublish}
                    title={
                      !canPublish ? "Tu plan no permite crear propiedades." : "Crear propiedad"
                    }
                  >
                    <Button className="w-full" disabled={!canPublish}>
                      Crear propiedad
                    </Button>
                  </a>
                </div>
              </div>
              <div className="pointer-events-none absolute inset-px rounded-lg shadow-xs ring-1 ring-black/5"></div>
            </div>

            {/* Investigations Card */}

            <div className="min-w-0 [&>div]:mt-0 [&>div]:h-full [&>div]:max-w-none">
              {isTestEnabled ? (
                <CtaCard
                  title={test?.title}
                  subtitle={test?.subtitle}
                  description={test?.description}
                  ctaLabel={test?.ctaLabel}
                  ctaLink="/investigaciones"
                  ctaTrack="home-research-cta-clicked"
                  image={researchImage}
                />
              ) : (
                <CtaCard
                  title="Investigaciones digitales para renta confiable"
                  subtitle="¡Ya está disponible!"
                  description="Evalúa a tus prospectos con datos reales y gana $$ por cada investigación."
                  ctaLabel="Empezar a ganar"
                  ctaLink="/investigaciones"
                  ctaTrack="home-research-cta-clicked"
                  image={researchImage}
                />
              )}
            </div>

            {/* Subscription Card */}
            <div className="relative min-w-0 lg:col-span-2">
              <div className="absolute inset-px rounded-lg bg-white"></div>
              <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)]">
                <div className="px-8 pt-8 pb-3 sm:px-10 sm:pt-10 sm:pb-0 space-y-3">
                  <p className="mt-2 text-lg font-medium tracking-tight text-gray-950 max-lg:text-center">
                    Adquiere un plan y obtén acceso a todas las funcionalidades
                  </p>
                  <p className="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                    Empieza a crear propiedades, publicar en venta, comparte tus leads y obtén
                    acceso a todas las funcionalidades de Casas y Terrenos.
                  </p>
                  <div className="my-4">
                    <a href="/subscription" className="">
                      <Button variant="default" className="w-full">
                        Administrar plan
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute inset-px rounded-lg shadow-xs ring-1 ring-black/5"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
